import { AlertCircle, CheckCircle2, X } from 'lucide-react';
/**
 * Lightweight toast notification system.
 *
 * Usage (inside any component):
 *   const { showToast, ToastContainer } = useToast();
 *
 *   // then later:
 *   showToast('Saved successfully', 'success');
 *
 *   return (
 *     <>
 *       ...
 *       <ToastContainer />
 *     </>
 *   );
 *
 * Toasts auto-dismiss after 3s (success/info) or 6s (error).
 * Stack at bottom-right on desktop, bottom-centre on mobile.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

type ToastVariant = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
}

let _counter = 0;

export function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (message: string, variant: ToastVariant = 'success') => {
      const id = ++_counter;
      setToasts((prev) => [...prev, { id, message, variant }]);
      const duration = variant === 'error' ? 6000 : 3000;
      const timer = setTimeout(() => dismiss(id), duration);
      timers.current.set(id, timer);
    },
    [dismiss],
  );

  // Cleanup on unmount
  useEffect(() => {
    const t = timers.current;
    return () => {
      for (const timer of t.values()) clearTimeout(timer);
    };
  }, []);

  function ToastContainer() {
    if (toasts.length === 0) return null;
    return (
      <div
        aria-live="polite"
        aria-label="Notifications"
        className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 items-end pointer-events-none"
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    );
  }

  return { showToast, ToastContainer };
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastItem;
  onDismiss: (id: number) => void;
}) {
  const variantClasses: Record<ToastVariant, string> = {
    success: 'bg-surface border-success/30 text-foreground',
    error: 'bg-surface border-destructive/30 text-foreground',
    info: 'bg-surface border-border text-foreground',
  };
  const Icon =
    toast.variant === 'success' ? CheckCircle2 : toast.variant === 'error' ? AlertCircle : null;
  const iconClass = toast.variant === 'success' ? 'text-success' : 'text-destructive';

  return (
    <div
      role="status"
      className={[
        'pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ring-1 ring-border elevate',
        'min-w-[280px] max-w-sm',
        variantClasses[toast.variant],
      ].join(' ')}
    >
      {Icon && <Icon className={`size-4 shrink-0 mt-0.5 ${iconClass}`} strokeWidth={1.75} />}
      <span className="flex-1 leading-snug">{toast.message}</span>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        className="shrink-0 text-muted-foreground hover:text-foreground press -mr-1"
      >
        <X className="size-3.5" strokeWidth={1.75} />
      </button>
    </div>
  );
}
